import { moduleLabel, t } from '@ctcells/i18n'

import type { ChamberPhase } from '../../figures/chamber3d/chamberScene.ts'
import type { ChamberStageProgress } from '../../figures/chamberStageLook.ts'

import type { ChamberProgress } from './chamberProgress.ts'
import { trackingChamberNote } from './trackingProgress.ts'
import {
  bundleDeviation,
  chamberFill,
  chamberPhase,
  chamberStageNote,
  type ChamberStageNote,
  NOTE_DEVICE,
} from './flowVocabulary.ts'
import type { SampleBundle } from './patientChambers.ts'
import { referenceLast, type FlowCell } from './sampleFlowModel.ts'

/**
 * 시료 카드 한 장이 **챔버에 무엇을 입히나** — 카드(`SampleBundles`)와 모니터링 시료 서랍
 * (`MonitoringSampleDrawer`)이 **같이 부르는 한 벌**이다.
 *
 * 🔴 **두 곳이 다른 말을 하지 않게 하려고 여기로 뺐다**(2026-09-28 · 서랍에 같은 챔버를 크게 세우며).
 *    종전에는 이 계산이 `BundleCard` 안에 있었다 — 서랍이 제 계산을 따로 두면 카드는 「염색기 진행」,
 *    서랍은 「염색기 완료」라고 말하는 날이 온다. 서랍에서 이 계산을 다시 짜지 마라.
 */
export interface BundleCardModel {
  /** 우물 안 공정 모양(배치 표지 기준 · 폐기·실패는 멈춘 자리로 언다) */
  phase?: ChamberPhase
  /** 챔버 아래 한 마디 — 모듈 칩 + 낱말 */
  note?: ChamberStageNote
  /** 시료가 없는 묶음의 까닭 낱말. 있으면 단계 대신 이 낱말이 선다 */
  vacancy?: string
}

/**
 * **두 열로 세우는 장수의 상한** — eric 2026-09-23 「시료 화면에 2열 3행으로」(= 6장)를 그대로 지키는 경계. 🔴 이 한 곳이다.
 * 넘으면 세 열이다(eric 2026-10-11 — 카드 = 시료 하나라 배치 하나가 9장이 되고, 두 열 5줄로는 줄 키가 카드의 글자 몫
 * (`--bundle-card-chrome` ≈ 151px · 1920 실측)보다 작아 **챔버 그림이 0px** 이 됐다). 카드가 챔버 하나짜리라 세 열 폭(≈200px)이 넉넉하다.
 * ⚠ 장수로 가른다 — 화면 키를 재지 않는다(고정 레이아웃 · 해상도 숫자 금지).
 */
export const BUNDLE_TWO_COLUMN_MAX = 6

/** 카드 격자의 열 수 — 6장 이하는 두 열(eric 09-23) · 넘으면 세 열 */
export function bundleColumns(count: number): 2 | 3 {
  return count > BUNDLE_TWO_COLUMN_MAX ? 3 : 2
}

/**
 * **한 화면에 세울 줄 수**(`--bundle-fit-rows`) — 열 수로 나눈 장수(올림) · 최소 3(종전 큰 카드 판).
 * 🔴 줄 수가 장수를 따라가야 판 키를 그만큼 나눠 다 든다(종전엔 CSS 가 3 · 4 로 박아 넘치는 줄이 굴렀다).
 */
export function bundleFitRows(count: number): number {
  return Math.max(3, Math.ceil(count / bundleColumns(count)))
}

/** 대조는 맨 뒤 — 카드 격자의 차례(eric 2026-09-28). 서랍의 순번도 이 차례로 센다 */
export function orderBundles(bundles: readonly SampleBundle[]): SampleBundle[] {
  return referenceLast(bundles, (bundle) => bundle.role === 'reference')
}

/**
 * **카드 머리의 순번**(「시료 1」) — 대조·시료 없는 묶음·자리 카드는 번호를 안 먹는다.
 * ⚠ 서버 값이 아니라 **모니터링 화면 안의 차례**다. 이 화면(카드 · 그 서랍) 밖으로 들고 가지 마라.
 */
export function bundleSequence(ordered: readonly SampleBundle[]): Map<string, number> {
  const seqOf = new Map<string, number>()
  for (const bundle of ordered) {
    if (bundle.role === 'reference' || bundle.sampleId === undefined || bundle.placeholder === true)
      continue
    seqOf.set(bundle.key, seqOf.size + 1)
  }
  return seqOf
}

/**
 * 배치가 받은 마지막 표지의 자리 — 카드가 「배치와 다른가」를 잴 기준.
 * 🔴 배치 단계 줄과 같은 값에서 나온다(둘이 갈리면 한 화면이 두 말을 한다).
 */
export function batchStageOf(batchStages: readonly FlowCell[]): number {
  return batchStages.reduce((last, cell, index) => (cell.marked ? index : last), -1)
}

export function bundleCardModel(
  bundle: SampleBundle,
  batchStages: readonly FlowCell[],
): BundleCardModel {
  const batchStage = batchStageOf(batchStages)
  /*
   * 🔴 **공정 자리**(단계별 챔버 모양 · eric 2026-09-23). 배치 표지가 기준이고, 배치와 다른
   *    시료는 **그 시료가 멈춘 자리**로 언다 — 폐기된 시료가 배치를 따라 염색까지 가면 거짓이다.
   */
  const frozen = deviationFreeze(bundle, batchStage)
  const phase = chamberPhase(batchStages, frozen)

  const deviation = bundleDeviation({
    state: bundle.state,
    ...(bundle.status === undefined ? {} : { status: bundle.status }),
    sampleStage: sampleStageIndex(bundle),
    batchStage,
  })

  /*
   * 🔴 **폐기·실패면 «멈춘 모듈»에 그 낱말이 선다.** 얼린 자리가 있으면 그것, 없으면 «그 시료의»
   *    마지막 표지 자리다 — `deviationFreeze` 는 배치보다 뒤처진 것만 얼리므로, 이 대비가 없으면
   *    배치와 같은 자리에서 실패한 시료의 「실패」가 화면에서 통째로 사라진다.
   */
  const halt =
    deviation === undefined
      ? undefined
      : { at: frozen ?? sampleStageIndex(bundle), word: deviation.word, tone: deviation.tone }
  const note = chamberStageNote(batchStages, halt)
  const vacancy = vacancyWord(bundle)

  return {
    ...(phase === undefined ? {} : { phase }),
    ...(note === undefined ? {} : { note }),
    ...(vacancy === undefined ? {} : { vacancy }),
  }
}

/**
 * 챔버 진행 → 2D 그림의 진행 어휘(`ChamberStageProgress`).
 * 🔴 **모듈 근거의 「진행」은 `stage` 다**(eric 2026-10-06 「시료별 하나씩 … 다 같이 움직여」). 서버가 어느 챔버를 처리 중인지
 *    안 주는 구간(원심 · 어셈블러 · 분주 기록을 못 받은 염색 — BR-98)에 아홉 칸이 같이 굵은 테 · 숨쉬는 도구를 세우지 않는다.
 *    웰 근거(`basis: 'well'`)와 나머지 상태는 그대로 넘긴다 — 서버가 웰을 말하면 칸마다 「진행」이 선다.
 */
export function figureProgress(progress: ChamberProgress): ChamberStageProgress {
  /*
   * 🔴 **6축 이송 중(`transfer`)도 `stage` 다**(eric 2026-10-06 · 팀장 (가)안). 「준비 중(점선)」으로 바꾸지 않고 진행 칠도 하지
   *    않는다 — 우물 안은 마지막 표지의 단계 모습 그대로다(`chamberPhase` 가 그 단계를 준다). 그림 층은 손대지 않는다.
   */
  if (progress.status === 'transfer' || progress.status === 'settled') return 'stage'
  return progress.basis === 'module' && progress.status === 'active' ? 'stage' : progress.status
}

/*
 * 묶음 위 한 줄(「원심분리기 진행 중」 · 「6축 로봇 · 이송 중」 · `bundlesBasisNote`)은 걷었다(eric 2026-10-13 「시료 위에 왜 원심분리기
 * 진행 중.. 이런 건 왜 나와?」) — 지금 도는 장비는 상시 작업 줄이 말한다.
 */

/**
 * **챔버 한 개 아래의 한 마디** — 카드 한 마디(`BundleCardModel.note`)를 그 챔버의 진행(`ChamberDot.progress`)으로 가른다.
 * 카드(`SampleBundles`)와 서랍(`MonitoringSampleDrawer`)이 같은 `BundleChamber` 를 거쳐 이 함수를 쓴다.
 *
 * 🔴 **갈리는 것은 서버가 그 웰을 가리킬 때뿐이다**(`basis: 'well'` — 염색 분주 기록). 모듈 근거면 카드 한 마디 그대로이되,
 *    **「진행」만은 공정명으로 접는다**(`status: 'stage'` · eric 2026-10-06). ⚠ 종전 이 자리의 「원심분리기처럼 모든 챔버가 함께
 *    도는 단계는 같은 말이 맞다」는 **틀렸다** — 원심은 회차마다 챔버 넷 · 넷 · 하나로 따로 돈다(레시피 centrifuge 블록 `chambers`).
 *    서버가 그 구분을 안 줄 뿐이다(BR-98).
 * 🔴 **시료의 편차(폐기 · 실패 · 뒤처짐)가 이긴다** — 그 시료가 멈췄으면 그 챔버의 분주 여부보다 그 사실이 먼저다.
 */
const CHAMBER_WORD = {
  pending: 'monitoring.chamber.ready',
  active: 'monitoring.chamber.doing',
  done: 'monitoring.chamber.done',
  failed: 'monitoring.flow.cell.fail',
  /* 웰 근거에는 오지 않는다(이송은 모듈 근거) — 표가 어휘 전부를 덮게 둔다 */
  transfer: 'monitoring.chamber.transfer',
  settled: 'monitoring.chamber.ready',
} as const

/** 낱말과 같은 표 — 배지 종류(`ChamberStageNote.status`). 실패는 편차(`tone`)가 정한다 */
const CHAMBER_STATUS = {
  pending: 'ready',
  active: 'doing',
  done: 'done',
  transfer: 'transfer',
  settled: 'stage',
} as const

export function chamberNote(
  cardNote: ChamberStageNote | undefined,
  progress: ChamberProgress | undefined,
): ChamberStageNote | undefined {
  if (progress?.basis === 'tracking') return trackingChamberNote(progress)
  if (cardNote === undefined) return undefined
  if (cardNote.tone !== undefined) return cardNote
  /*
   * 🔴 **모듈 근거의 「진행」은 챔버마다 칠하지 않는다**(eric 2026-10-06 「시료별 하나씩 상태 바뀌게 … 다 같이 움직여」).
   *    원심 · 어셈블러는 회차 · 챔버마다 따로 돌지만(레시피 블록 스텝이 `chambers` 를 싣는다) 서버는 «어느 챔버를 처리 중인지»를
   *    안 준다(BR-98). 그 구간에 아홉 칸 전부 「진행」이면 그 순간 안 도는 챔버까지 「진행」이라는 거짓이 섞인다.
   *    ⇒ 공정명만 남기고 배지를 뺀다(`status: 'stage'` — 「그 단계에 와 있음」). 「진행 중」은 묶음 위 한 줄
   *    (`bundlesBasisNote` · `SampleBundles`)이 «한 번만» 말한다.
   * 🔴 **준비 중 · 완료는 그대로다** — 등록 · 단계 완료 표지는 배치 전건에 같이 찍히는 사실이라 칸마다 같아도 참이다.
   * 🔵 서버가 웰을 가리켜 말하면(`basis: 'well'`) 아래 갈래로 칸마다 칠한다 — BR-98 값이 오면 `chamberProgress` 가
   *    그 구간을 `well` 로 내면 된다. 이 갈래는 손댈 필요가 없다.
   */
  /*
   * 🔵 **트레이 근거** — 서버 트레이 뷰가 그 챔버의 자리를 말했다(`trayChamberProgress`). 칸마다 「준비 중 · 진행 · 완료」를
   *    그 자리의 공정 장비 칩과 함께 적는다. 분주 라벨·스포이드(웰 근거)는 붙지 않는다 — 자리는 알아도 분주는 모른다.
   */
  if (progress?.basis === 'tray' && progress.status !== 'transfer' && progress.status !== 'settled')
    return {
      device: NOTE_DEVICE[progress.stage],
      word: t(CHAMBER_WORD[progress.status]),
      ...(progress.status === 'failed'
        ? { tone: 'fail' as const }
        : { status: CHAMBER_STATUS[progress.status] }),
    }
  if (progress?.basis !== 'well') {
    /*
     * 🔴 **6축 이송 중**(팀장 (가)안 2026-10-06 · 히나 재현 `8c8531c0`). 원심 실행이 끝난 뒤 `centrifuged` 표지가 오기 전
     *    (약 40초)에 카드 한 마디가 마지막 표지(`registered`)로 되돌아가 「원심분리기 · 준비 중」이 섰다 — 다 돈 원심을 «준비 중»이라
     *    말하는 거짓이었다. 어셈→염색 앞 틈(약 10초)의 「원심분리기 완료」도 같은 갈래다. 이송은 트레이째라 전 챔버에 같은 사실이다.
     * 🔴 근거는 구독 진행 프레임의 `module_type`(6축) 하나다(`chamberProgress` 의 `transfer`). 표지를 앞당기지 않는다.
     * ⚠ 편차(폐기 · 실패 · 뒤처짐)는 위에서 이미 이겼다 — 멈춘 시료에는 「이송 중」을 세우지 않는다.
     */
    if (progress?.status === 'transfer')
      return { device: 'robot', word: t('monitoring.chamber.transfer'), status: 'transfer' }
    /*
     * 🔴 **실행 끝 · 등록 표지뿐**(`settled` · `chamberProgress` 표) — 공정명만(배지 없음). 「준비 중」도 「완료」도 아니다.
     *    원심 실행이 끝나고 이송 락이 보이기 전 1~2초의 틈이다(2026-10-06 실측).
     */
    if (progress?.status === 'settled')
      return { device: cardNote.device, word: moduleLabel(cardNote.device), status: 'stage' }
    if (progress?.basis === 'module' && progress.status === 'active' && cardNote.status === 'doing')
      return { device: cardNote.device, word: moduleLabel(cardNote.device), status: 'stage' }
    return cardNote
  }
  /*
   * 🔴 **화면 글자는 기존 챔버 낱말을 그대로 잇는다**(eric 2026-09-28 「아직 말고 준비 중 이라고 표현해야지」).
   *    `pending` → 「준비 중」 · `active` → 「진행」 · `done` → 「완료」 · `failed` → 「실패」. 「아직」·「대기」를 쓰지 마라.
   */
  /*
   * 🔴 **칩은 모듈이 아니라 그 단계(「분주」)를 말한다**(eric 2026-09-28). 웰 근거의 「완료」는 «이번 분주 한 바퀴에서
   *    그 웰이 받았다»이지 염색기가 끝났다는 말이 아니다 — 「염색기 완료」로 찍으면 모듈이 도는 중에 거짓이 선다.
   */
  /*
   * 🔴 **피펫이 지금 이 웰에 있으면 「분주 중」 배지 하나**(eric 2026-10-14 「분주 완료만 있고.. 진행 중인 건 왜 하나도 없어?
   *    피펫으로 할 때.. 분주 중이라고 해야지」). `active` 는 화면이 짐작한 것이 아니라 피펫 자리(서버 사실)를 얹은 것이다
   *    (`withPipetteActive`). 「분주 · 분주 중」으로 두 번 적지 않게 칩 이름은 비운다.
   */
  if (progress.status === 'active')
    return {
      device: 'stain',
      label: '',
      word: t('monitoring.chamber.dispensing'),
      status: CHAMBER_STATUS.active,
    }
  return {
    device: 'stain',
    label: t('monitoring.chamber.dispense'),
    word: t(CHAMBER_WORD[progress.status]),
    ...(progress.passStep === undefined
      ? {}
      : { title: t('monitoring.chamber.dispenseStep', { step: progress.passStep }) }),
    ...(progress.status === 'failed'
      ? { tone: 'fail' as const }
      : { status: CHAMBER_STATUS[progress.status] }),
  }
}

/**
 * **이 챔버가 «지금 작업 중»인가** — 2026-10-01 CTCells 회의 「9개 중 현재 작업 중인 칩이 구분되어야 함」.
 *
 * 🔴 **새로 판정하지 않는다.** 챔버 아래 한 마디(`chamberNote`)가 「진행」(`status: 'doing'`)일 때가 곧 작업 중이다 —
 *    그 낱말의 출처는 ⓐ 모듈 근거면 배치 단계 줄의 진행 칸(구독 진행 프레임이 말한 «지금 도는 장비») ⓑ 웰 근거면
 *    서버가 그 웰을 가리켜 말한 분주 기록이다. 강조와 낱말이 같은 값에서 나오므로 둘이 갈리지 않는다.
 * 🔴 **편차(폐기 · 실패 · 뒤처짐)는 작업 중이 아니다** — 그때 한 마디는 편차 낱말이다(`tone`).
 * 🔴 **배정을 모르는 챔버는 아니다** — 누구 것인지 모르는 챔버에 「작업 중」을 얹지 않는다(`BundleChamber` 의 `shown` 규칙).
 * 🔴 **스트림이 끊기면(`stale`) 아무 챔버도 아니다** — 마지막으로 안 «지금»을 현재처럼 보이지 않는다.
 * 🔴 **모듈 근거(원심 · 어셈블러)는 작업 중이 아니다**(eric 2026-10-06 「시료별 하나씩 상태 바뀌게 … 다 같이 움직여」).
 *    종전(2026-10-01)에는 서버가 웰을 말하지 않아 «배정된 챔버 전부»가 작업 중으로 섰다 — 그 순간 안 도는 챔버까지 섞인 거짓이었다.
 *    이제 그 구간의 한 마디는 `stage`(공정명만)라 이 판정이 저절로 거짓이 되고, 「진행 중」은 묶음 위 한 줄(`bundlesBasisNote`)이 말한다.
 *    서버가 «처리 중인 웰»을 주면(BR-98 · `basis: 'well'` · `active`) 그 칩만 작업 중으로 선다 — 이 함수는 손댈 필요가 없다.
 * ⚠ 염색 분주 중에는 서버가 «지금 분주 중인 웰»을 안 준다(`chamberProgress.ts` 머리말) — 다음 웰을 차례로 짐작하지 않는다.
 *   피펫 자리(모듈 스냅숏 · `StainPipetteWellContext`)가 웰을 가리킬 때만 그 칸이 「분주 중」 · 작업 중이다(`withPipetteActive` · 2026-10-14).
 */
/**
 * 화면의 카드 전부를 놓고 **강조할 작업 중 챔버**를 고른다 — 카드 열쇠(`bundle.key`) → 웰 이름 집합.
 *
 * 🔴 **배정된 챔버가 전부 작업 중이면 강조를 걷는다**(eric 2026-10-14 「염색 단계 보면.. 모든 것이 작업 중이라.. 다 체크되어
 *    있잖아.. 계속 포커싱이니 보기가 불편해 복잡하고」). 강조의 쓸모는 「아홉 중 어디가 도는지」를 가르는 것인데(2026-10-01 회의),
 *    전부 같으면 가를 것이 없고 테 아홉 · 카드 테 다섯이 화면을 덮을 뿐이다. 「진행」 한 마디는 챔버마다 그대로 선다.
 * 🔴 그때 **피펫이 지금 들어가 있는 웰**(`pipetteWell` · 서버 사실 · `StainPipetteWellContext`)이 있으면 그 한 칸만 남긴다 —
 *    전부 도는 가운데 «지금 손이 닿은 곳»은 여전히 하나다. 짐작하지 않는다 — 모르면(`null`) 아무 칸도 아니다.
 * ⚠ 한 칸만 배정된 화면(1칸 이하)은 걷지 않는다 — 그 한 칸이 도는지가 곧 정보다.
 */
export function focusedWorking(input: {
  bundles: readonly SampleBundle[]
  batchStages: readonly FlowCell[]
  stale: boolean
  pipetteWell: string | null
}): Map<string, ReadonlySet<string>> {
  const { bundles, batchStages, stale, pipetteWell } = input
  const out = new Map<string, Set<string>>()
  let assigned = 0
  let working = 0
  for (const bundle of bundles) {
    if (bundle.placeholder === true) continue
    const { note } = bundleCardModel(bundle, batchStages)
    const wells = new Set<string>()
    for (const dot of bundle.chambers) {
      if (chamberFill(dot) === 'unassigned') continue
      assigned += 1
      if (chamberWorking({ dot, note, stale })) {
        working += 1
        wells.add(dot.well)
      }
    }
    out.set(bundle.key, wells)
  }
  if (assigned <= 1 || working < assigned) return out
  /* 전부 작업 중 — 피펫 칸 하나만 남긴다(없으면 아무 칸도) */
  for (const [key, wells] of out)
    out.set(
      key,
      pipetteWell !== null && wells.has(pipetteWell) ? new Set([pipetteWell]) : new Set(),
    )
  return out
}

export function chamberWorking(input: {
  dot: SampleBundle['chambers'][number]
  /** 카드 한 마디(`bundleCardModel().note`) */
  note: ChamberStageNote | undefined
  stale: boolean
}): boolean {
  const { dot, note, stale } = input
  if (stale || chamberFill(dot) === 'unassigned') return false
  if (dot.progress?.basis === 'tracking') return dot.progress.status === 'active'
  return chamberNote(note, dot.progress)?.status === 'doing'
}

/**
 * 그 시료가 **마지막으로 받은 표지의 자리**. 배치와 견주는 기준이다.
 * 🔴 배정을 모르면(`stages` 가 빈 배열) `-1` — 「아무 표지도 없다」이지 0(등록)이 아니다.
 */
function sampleStageIndex(bundle: SampleBundle): number {
  let last = -1
  bundle.stages.forEach((cell, index) => {
    if (cell.marked) last = index
  })
  return last
}

/**
 * 그 시료가 **배치와 달라 «언» 자리** — 폐기·실패면 그 시료가 멈춘 단계, 아니면 `undefined`.
 * 🔴 배치와 같은 시료는 배치를 그대로 따라간다(한 화면이 두 말을 하지 않는다).
 */
function deviationFreeze(bundle: SampleBundle, batchStage: number): number | undefined {
  const own = sampleStageIndex(bundle)
  if (bundle.status !== 'discarded' && bundle.state.verdict !== 'fail') return undefined
  return own < 0 || own >= batchStage ? undefined : own
}

/**
 * 묶음 카드에 시료가 없는 까닭의 **낱말 하나** — 없으면 `undefined`.
 * 🔴 「미배정」(규칙은 돌았는데 이 묶음에 든 시료가 없다)과 「배정 모름」(규칙 자체가 없다)은
 *    다른 사실이다. 하나로 접지 마라(`SampleBundle.vacancy`).
 */
function vacancyWord(bundle: SampleBundle): string | undefined {
  switch (bundle.vacancy) {
    case 'vacant':
      return t('monitoring.flow.groups.vacant')
    case 'unknown':
      return t('monitoring.flow.groups.assignUnknown')
    default:
      return undefined
  }
}

/**
 * 한 묶음의 시료 번호들 — 마지막 «-» 까지 같은 앞부분은 한 번만 적고 뒷자리를 「·」로 잇는다(`TEST-…-01 · 02` · 2026-10-14).
 * 앞부분이 다르면 번호를 그대로 잇는다. 🔴 자르지 않는다(대조하는 뜻 · `SampleId` 머리말).
 */
export function sampleIdsText(ids: readonly string[]): string {
  const [first, ...rest] = ids
  if (first === undefined) return ''
  if (rest.length === 0) return first
  const cut = first.lastIndexOf('-') + 1
  const head = first.slice(0, cut)
  if (cut > 0 && rest.every((id) => id.startsWith(head) && id.length > head.length))
    return [first, ...rest.map((id) => id.slice(cut))].join(' · ')
  return ids.join(' · ')
}
